import type { TrelloWindow } from '@trello/window-types';
import type {
  RpcModelType,
  RpcUpdatePayload,
  RpcDeletePayload,
} from './rpc.types';
import { AjaxRPC } from './AjaxRPC';
import { SocketRPC } from './SocketRPC';
import { ApiError } from 'app/scripts/network/api-error';
import { Auth } from 'app/scripts/db/auth';
import Backbone from '@trello/backbone';
// eslint-disable-next-line no-restricted-imports
import { Batcher } from 'app/scripts/network/rpc/batcher';
import BluebirdPromise from 'bluebird';
import { TrelloStorage } from '@trello/storage';
import { Util } from 'app/scripts/lib/util';
import _ from 'underscore';
import { Analytics } from '@trello/atlassian-analytics';
import { featureFlagClient } from '@trello/feature-flag-client';
import { sendErrorEvent } from '@trello/error-reporting';
declare const window: TrelloWindow;

window.activateWebSockets = () => {
  TrelloStorage.set('useWebsockets', 'yes');
  // eslint-disable-next-line no-console
  console.log(
    'Websockets engaged! Reload the page, please. Use deactiveWebSockets() to turn sockets off.',
  );
};

window.deActivateWebSockets = () => {
  TrelloStorage.set('useWebsockets', 'no');
  // eslint-disable-next-line no-console
  console.log(
    'Shutting Websockets down! Reload the page, please. Use activateWebSockets() to turn sockets back on.',
  );
};

const VERIFY_INTERVAL_MS = 20 * 1000;
const INVALID_MODEL_ERRORS = ['unauthorized', 'forbidden', 'not found'];

interface Model {
  tags: string[];
  ixLastUpdate: number;
  modelType: string;
  idModel: string;
}

type SocketRPCInstance = InstanceType<typeof SocketRPC>;
type AjaxRPCInstance = InstanceType<typeof AjaxRPC>;
type RPCInstance<T extends SocketRPCInstance | AjaxRPCInstance> = T;
type BackboneEvents = typeof Backbone.Events;
interface RPC extends BackboneEvents {
  on(type: 'updateModels', callback: (payload: RpcUpdatePayload) => void): this;
  on(type: 'deleteModels', callback: (payload: RpcDeletePayload) => void): this;
  on(
    type: 'invalidModel',
    callback: (typeName: RpcModelType, id: string) => void,
  ): this;
  on(type: 'ready', callback: () => void): this;
  off(
    type: 'updateModels',
    callback: (payload: RpcUpdatePayload) => void,
  ): this;
}
class RPC {
  rpcSocket: SocketRPCInstance | null;
  rpcAJAX: AjaxRPCInstance | null;
  rpcCurrent:
    | RPCInstance<SocketRPCInstance>
    | RPCInstance<AjaxRPCInstance>
    | null;
  currentSubscriptions: Record<string, Model>;
  rpcConnectTime: number;
  socketRetryWaitTime: number;
  blockSocketReconnection: boolean;
  handledSubscriptionErrors: string[];
  _verifyTimeout?: number;

  static initClass() {
    _.extend(this.prototype, Backbone.Events);
  }

  // We want to immediately open up an rpc channel, so we use AJAX until the socket has connected
  // and proven that it can answer a ping. If the socket connects and answers quickly, we use
  // that for all future requests. If not, we just close it down and keep using AJAX. We defer
  // setting up anything that will require polling on the AJAX rpc until we either confirm or
  // give up on the socket rpc.
  constructor() {
    this.rpcSocket = this.rpcAJAX = this.rpcCurrent = null;
    this.currentSubscriptions = {};

    this.rpcConnectTime = 0;
    this.socketRetryWaitTime = 0;
    this.blockSocketReconnection = false;
    this.handledSubscriptionErrors = INVALID_MODEL_ERRORS;
    // Always start an RPC if we're logged in, so we can get our notifications
    this.handleLogin();

    setTimeout(() => {
      return this.verifySubscriptions();
    }, VERIFY_INTERVAL_MS);

    featureFlagClient.on<boolean>(
      'fep.disconnect_active_clients',
      false,
      (disconnectActiveClients) => {
        if (disconnectActiveClients === true) {
          this.blockSocketReconnection = true;
          this.rpcAJAX?.stopPolling?.();
        }
      },
    );
  }

  rpcReady() {
    if (!this.isUsingSocket()) {
      this.rpcAJAX?.poll?.();
    }

    this.trigger('ready');

    this.resubscribe();

    return this.listenTo(this, 'reconnect', () => {
      return this.resubscribe();
    });
  }

  resubscribe() {
    return (() => {
      const result = [];
      for (const idModel in this.currentSubscriptions) {
        const subscription = this.currentSubscriptions[idModel];
        result.push(
          this.subscribe(subscription.modelType, idModel, subscription.tags),
        );
      }
      return result;
    })();
  }

  setActiveRPC(rpc: AjaxRPCInstance | SocketRPCInstance) {
    this.stopListening(this.rpcCurrent);
    this.rpcCurrent = rpc;

    this.listenTo(this.rpcCurrent, 'all', (...args: unknown[]) => {
      // @ts-expect-error
      return this.trigger(...Array.from(args || []));
    });
  }

  lazyCreateRPC() {
    if (
      this.blockSocketReconnection === true ||
      this.isUsingSocket() ||
      Util.getTime() - this.rpcConnectTime < this.socketRetryWaitTime
    ) {
      return;
    }

    const traceId = Analytics.startTask({
      taskName: 'create-session/socket',
      source: 'network:rpc',
    });

    this.rpcConnectTime = Util.getTime();
    this.socketRetryWaitTime = Util.randomWait(
      // @ts-expect-error convert Util to ts
      Util.getMs({ minutes: 20 }),
      // @ts-expect-error convert Util to ts
      Util.getMs({ minutes: 10 }),
    );

    // Create a new socket instance to force new Socket creation
    this.rpcSocket = new SocketRPC(this);
    if (this.rpcAJAX) {
      this.rpcAJAX.stopPolling();
    } else {
      this.rpcAJAX = new AjaxRPC(this);
    }

    // Until the socket proves it works, you're on AJAX rpc.
    this.setActiveRPC(this.rpcAJAX);

    if (this.rpcSocket.hasSocketTransport()) {
      // Sockets have a chance of working. They have 8s to answer a ping before we give up on 'em.
      const msPingWait = 8000;

      return this.rpcSocket.ping((err, result, pingedSocket) => {
        const pingElapsedTime = Util.getTime() - this.rpcConnectTime;
        if (!!err || pingElapsedTime > msPingWait) {
          // Socket took too long. Use AJAX.

          // by the time we get here, @rpcCurrent may already be pointing at
          // a different socket than the one we just pinged
          pingedSocket?.disconnect?.();
          pingedSocket?.destroy?.();

          this.rpcSocket = null;

          Analytics.sendOperationalEvent({
            action: 'errored',
            actionSubject: 'socketPing',
            source: 'network:rpc',
            attributes: {
              taskId: traceId,
              status: err ? 'failed' : 'slow',
              message: err instanceof Error ? err.message : 'error: slow ping',
              pingElapsedTime,
            },
          });

          Analytics.taskFailed({
            taskName: 'create-session/socket',
            source: 'network:rpc',
            traceId,
            error: new Error('slow ping'),
          });

          this.rpcReady();
          setTimeout(() => {
            return this.lazyCreateRPC();
          }, this.socketRetryWaitTime);
        } else {
          // Yay, we have sockets!
          // @ts-expect-error im not sure how this is set by the time it gets here, but it is
          this.setActiveRPC(this.rpcSocket);
          this.rpcReady();
          Analytics.taskSucceeded({
            taskName: 'create-session/socket',
            source: 'network:rpc',
            traceId,
          });
        }
      });
    } else {
      // We do not have an acceptable socket transport. Use AJAX.
      Analytics.sendOperationalEvent({
        action: 'errored',
        actionSubject: 'unsupportedSocketConnection',
        source: 'network:rpc',
        attributes: {
          taskId: traceId,
        },
      });
      Analytics.taskFailed({
        taskName: 'create-session/socket',
        source: 'network:rpc',
        traceId,
        error: new Error('Unsupported socket connection'),
      });
      return this.rpcReady();
    }
  }

  handleLogin() {
    if (Auth.isLoggedIn()) {
      return this.lazyCreateRPC();
    }
  }

  unsubscribe(modelType: string, idModel: string) {
    this.lazyCreateRPC();
    this.rpcCurrent?.unsubscribe?.(modelType, idModel);
    return delete this.currentSubscriptions[idModel];
  }

  _subscribeOrHandle(modelType: string, idModel: string, tags: string[]) {
    return BluebirdPromise.fromNode((next) => {
      return this.rpcCurrent?.subscribe?.(modelType, idModel, tags, next);
    }).catch((err) => {
      if (INVALID_MODEL_ERRORS.includes(err.message)) {
        this.handleInvalidSubscription(idModel);
        // @ts-expect-error
        return this.trigger('subscription_invalid', modelType, idModel, tags);
      }
    });
  }

  subscribe(modelType: string, idModel: string, tags: string[]) {
    let subscription;
    this.lazyCreateRPC();

    if ((subscription = this.currentSubscriptions[idModel])) {
      subscription.tags = _.union(subscription.tags, tags);
    } else {
      this.currentSubscriptions[idModel] = {
        tags,
        ixLastUpdate: -1,
        modelType,
        idModel,
      };
    }

    return this._subscribeOrHandle(modelType, idModel, tags);
  }

  getIxLastUpdate(idModel: string) {
    return this.currentSubscriptions[idModel]
      ? this.currentSubscriptions[idModel].ixLastUpdate
      : undefined;
  }

  setIxLastUpdate(idModel: string, ixLastUpdate: number) {
    let entry;
    if ((entry = this.currentSubscriptions[idModel])) {
      return (entry.ixLastUpdate = ixLastUpdate);
    }
  }

  stop() {
    if (this.isUsingSocket()) {
      this.rpcSocket?.destroy?.();
    } else {
      this.rpcAJAX?.stopPolling?.();
    }
  }

  isUsingSocket() {
    return !!this.rpcCurrent && this.rpcCurrent === this.rpcSocket;
  }

  handleInvalidSubscription(idModelInvalid: string) {
    let subscription;
    if ((subscription = this.currentSubscriptions[idModelInvalid])) {
      const { modelType, idModel, tags } = subscription;

      this.unsubscribe(modelType, idModel);
      // @ts-expect-error
      this.trigger('invalidModel', modelType, idModel, tags);
    }
  }

  isGuestOrganization(modelType: string, idModel: string) {
    if (modelType !== 'Organization') return false;
    const me = Auth.isLoggedIn() ? Auth.me() : null;
    const idOrganizations = me && me.get('idOrganizations');
    const idBoardOrganizations =
      me?.boardList?.models
        ?.filter((board) => board.isOpen() && board.get('idOrganization'))
        .map((board) => board.get('idOrganization')) || [];
    const idGuestOrganizations = idOrganizations
      ? _.difference(idBoardOrganizations, idOrganizations)
      : idBoardOrganizations;
    if (idGuestOrganizations.includes(idModel)) {
      return true;
    }
  }

  _verificationUrls() {
    const subscriptions = Object.values(this.currentSubscriptions);

    const me = Auth.isLoggedIn() ? Auth.me() : null;

    return subscriptions
      .map(({ modelType, idModel }) => {
        const url = (() => {
          let idBoards: string[] | null;
          let idOrganizations: string[] | null;
          switch (modelType) {
            case 'Board':
              idBoards = me && me.get('idBoards');
              if (idBoards && Array.from(idBoards).includes(idModel)) {
                // If it's in our idBoards we're allowed to see it
                return null;
              } else {
                return `/boards/${idModel}/name`;
              }
            case 'Member':
              if (idModel === Auth.myId()) {
                // Don't bother checking if we can read our member channel
                return null;
              } else {
                return `/members/${idModel}/fullName`;
              }
            case 'Organization':
              idOrganizations = me && me.get('idOrganizations');
              if (
                (idOrganizations &&
                  Array.from(idOrganizations).includes(idModel)) ||
                this.isGuestOrganization(modelType, idModel)
              ) {
                // If it's in our idOrganizations or we're a guest of
                // the organization (on a board owned by the org) we're
                // allowed to see it
                return null;
              } else {
                return `/organizations/${idModel}/displayName`;
              }
            default:
              return null;
          }
        })();

        if (url) {
          return { id: idModel, url };
        }
      })
      .filter((entry) => !!entry);
  }

  _sendVerificationRequests() {
    const verifyData = this._verificationUrls();

    if (verifyData.length === 0) {
      return BluebirdPromise.resolve();
    }

    const batcher = new Batcher({
      headers: {
        'X-Trello-Operation-Source': 'rpc',
        'X-Trello-Operation-Name': 'verificationRequests',
      },
    });

    verifyData.forEach((data) => {
      if (!data) {
        return;
      }
      const { id, url } = data;
      return batcher
        .request(url)
        .catch(ApiError, () => {
          return this.handleInvalidSubscription(id);
        })
        .catch(Batcher.BatchFailure, (batchError) => {
          // We'll handle the batch failure once, after the .send() call
        });
    });

    return batcher
      .send()
      .catch(ApiError.NoResponse, ApiError.Timeout, () => {
        // This is a connectivity problem that doesn't need to be reported, we'll just wait
        // for the next poll
      })
      .catch(ApiError.Unauthenticated, () => {
        // The whole request failed because we aren't authenticated anymore, we don't need
        // to report that
      })
      .catch(ApiError, (error: Error) => {
        sendErrorEvent(
          new Error(`Batch Failure while sending verification requests`),
          {
            extraData: {
              errorName: error.name,
              verifyData: JSON.stringify(verifyData),
            },
          },
        );
      });
  }

  // This is meant to catch some tricky situations where we might have lost
  // access to something even though we received no websocket update informing
  // us.  For example, a public board or team that we're looking at may have
  // been deleted, or a team visible board that we aren't a member of might
  // have been changed to private.
  verifySubscriptions() {
    clearTimeout(this._verifyTimeout);

    return this._sendVerificationRequests()
      .finally(() => {
        let timeout: number;
        return (timeout = this._verifyTimeout = window.setTimeout(() => {
          if (timeout === this._verifyTimeout) {
            return requestAnimationFrame(() => {
              return this.verifySubscriptions();
            });
          }
        }, VERIFY_INTERVAL_MS));
      })
      .done();
  }
}
RPC.initClass();

// eslint-disable-next-line @trello/no-module-logic
export const rpc = (window.rpc = new RPC());

// If we're unable to subscribe to our own member channel, something
// has gone wrong (possibly we deleted our account or our session was
// invalidated from another browser)
// eslint-disable-next-line @trello/no-module-logic
if (Auth.isLoggedIn()) {
  // eslint-disable-next-line @trello/no-module-logic
  rpc.on('invalidModel', function (typeName: string, id: string) {
    if (typeName === 'Member' && Auth.isMe(id)) {
      return Auth.removeToken();
    }
  });
}
